import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { ImageAsset } from '@/components/common'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo } from '@/components/common/typography'
import { couponApplyToastMessage } from '@/components/domains/order2/coupon-apply-toast-message'
import { ModalOrderProductCouponSelect } from '@/components/domains/order2/modal-order-product-coupon-select'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { IconSizeEnum } from '@/constants/icon-size.enum'
import useOrderPageEventTracker from '@/containers/event-tracker/order.event-tracker'
import { useModal } from '@/containers/hooks'
import { useSetEstimateOrder } from '@/containers/order/set-estimate-order.hook'
import { doSetGlobalToastContent } from '@/stores/reduxUI'
import { isDateBefore } from '@/utils/utilDate'
import { currencyText } from '@/utils/utilNumber'
import type { CouponForEstimateOrderScheme } from '@/apis/rest/generated/types'

interface ShipmentCouponSectionProps {
  countOfAvailableShippingCoupon: number
  isPartialOrderCouponDiscount: boolean
  shippingCoupons: CouponForEstimateOrderScheme[] | undefined
  productSaleAmount: number | undefined
  shippingIndex: number
}

export const ShipmentCouponSection = ({
  countOfAvailableShippingCoupon,
  isPartialOrderCouponDiscount,
  productSaleAmount = 0,
  shippingCoupons = [],
  shippingIndex,
}: ShipmentCouponSectionProps) => {
  const dispatch = useDispatch()
  const { color } = useTheme()
  const { useModalProps } = useModal()

  const { trackViewOrderCouponNew, trackClickOrderCouponApplyNew } = useOrderPageEventTracker()

  const { setEstimateOrderByShipmentCoupon } = useSetEstimateOrder()

  const appliedCoupon = shippingCoupons?.find((coupon) => coupon.isApplied)

  const handleShowModal = () => {
    if (shippingCoupons?.length <= 0) {
      dispatch(doSetGlobalToastContent({ content: '보유중인 쿠폰이 없어요' }))
      return
    }
    trackViewOrderCouponNew(shippingCoupons, productSaleAmount)
    useModalProps.showModal()
  }

  const handleApplyCoupon = (couponId: number | undefined) => {
    const selectedCoupon = shippingCoupons.find((coupon) => coupon.id === couponId)

    // 쿠폰 만료 시각 <= 현재 시각 => true
    if (selectedCoupon && isDateBefore(selectedCoupon.expiredAt, new Date().toISOString())) {
      dispatch(doSetGlobalToastContent({ content: '유효기간이 만료된 쿠폰이에요' }))
      setEstimateOrderByShipmentCoupon({ shippingIndex, shippingCouponId: undefined })
      return
    } else {
      const message = couponApplyToastMessage({
        appliedCouponExist: !!appliedCoupon,
        selectedCouponExist: !!selectedCoupon,
      })
      if (message) {
        dispatch(doSetGlobalToastContent({ content: message }))
        setEstimateOrderByShipmentCoupon({ shippingIndex: shippingIndex, shippingCouponId: couponId })
      }
    }

    if (selectedCoupon) {
      trackClickOrderCouponApplyNew(shippingCoupons, selectedCoupon, productSaleAmount)
    }
  }

  return (
    <>
      <StyledSectionTitle>
        <Typo variant={'Body1 Bold'}>배송비 쿠폰</Typo>
      </StyledSectionTitle>
      <StyledContainer>
        <StyledSelectBox type="button" onClick={handleShowModal}>
          {appliedCoupon && (
            <Typo variant="Body3 Bold" color={color.red500}>
              {`${currencyText(appliedCoupon.appliedDiscountAmount)} 할인됨`}{' '}
            </Typo>
          )}
          {!appliedCoupon &&
            (countOfAvailableShippingCoupon <= 0 ? (
              <Typo variant="Body1 regular" color={color.gray900}>
                사용 가능한 배송비 쿠폰이 없어요
              </Typo>
            ) : (
              <StyledSelectText>
                <FlatIcon type={IconNameEnum.IcOrderCoupon} size={IconSizeEnum.Size16} width={IconSizeEnum.Size20} />
                <Typo variant="Body3 Bold" color={color.red500}>
                  {`사용 가능한 쿠폰이 ${countOfAvailableShippingCoupon}장 있어요.`}
                </Typo>
              </StyledSelectText>
            ))}
          <ImageAsset name="ic_chevron_down_gray700_24" size={24} svg />
        </StyledSelectBox>
      </StyledContainer>
      <ModalOrderProductCouponSelect
        key={`shipment-coupon-${appliedCoupon?.appliedDiscountAmount}`}
        title="배송비쿠폰 선택"
        useModalProps={useModalProps}
        appliedCoupon={appliedCoupon}
        couponList={shippingCoupons}
        onSelectCoupon={handleApplyCoupon}
      />
    </>
  )
}

const StyledSectionTitle = styled.div`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 0 1.6rem 2rem;
`

const StyledSelectBox = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 5rem;
  border: 1px solid ${GlobalColorEnum.Gray100};
  border-radius: 0.8rem;
  box-sizing: border-box;
  padding: 1.6rem;
  cursor: pointer;
  background-color: ${(props) => props.theme.color.grayWhite};
`

const StyledSelectText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`
